import React, { FC, useEffect, useRef, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { InvestmentRoundingSetting } from 'shared';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { SafeHooks } from '../../../../hooks/index';
import { SafeConversionSummaryResponse } from '../../../../hooks/safe/index';
import { toRound } from '../../../../utils/get-rounded-number.util';
import { toMathRound } from '../../../../utils/to-math-round.util';
import { toNumber } from '../../../../utils/to-number.util';
import { FormSchema } from '../convert-modal.validation';
import { ConvertSafeItem } from './convert-safe-item';

const selectionKey = (
  companyId: string,
  valuationId: string | undefined,
  safeIds: string[],
  roundingSetting: InvestmentRoundingSetting | undefined,
) =>
  `${companyId}|${valuationId ?? ''}|${roundingSetting ?? InvestmentRoundingSetting.DEFAULT}|${[...safeIds].sort().join(',')}`;

export type ConversionSummaryWithId = {
  id: string;
  summary: SafeConversionSummaryResponse;
};

export type GeneralSummary = {
  convertedShares?: number;
  investment?: number;
  preMoneyValuation?: number;
  sharePrice?: number;
  totalOwned?: number;
};

export type ConversionSummaryProps = {
  allSafeIds?: string[];
  companyId: string;
  conversionSummary?: GeneralSummary;
  currencySign?: string;
  /** True while the current selection has no summary because the backend refused it. */
  onErrorChange?: (hasError: boolean) => void;
  onLoadingChange?: (loading: boolean) => void;
  postMoneyValuation?: number;
  preMoneyValuation?: number;
  roundingSetting?: InvestmentRoundingSetting;
  setValue: UseFormSetValue<FormSchema>;
  valuationId?: string;
  valuationIssuedShares?: number;
};

const ConversionDetailsLoading = () => (
  <div className="flex w-full flex-col gap-2 rounded-md border border-gray-100 bg-gray-50 p-4">
    <div className="flex items-center gap-2 text-sm font-[450] text-gray-500">
      <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-brand-600" />
      <AppFormattedMessage id="LOADING" />
    </div>
    <div className="flex flex-col gap-2">
      {[1, 2, 3].map((i) => (
        <div className="h-12 animate-pulse rounded bg-gray-200" key={i} />
      ))}
    </div>
  </div>
);

export const ConversionSummary: FC<ConversionSummaryProps> = ({
  currencySign,
  companyId,
  allSafeIds,
  setValue,
  conversionSummary,
  onErrorChange,
  onLoadingChange,
  preMoneyValuation,
  postMoneyValuation,
  roundingSetting,
  valuationId,
  valuationIssuedShares,
}) => {
  const [conversionSummaries, setConversionSummaries] = useState<ConversionSummaryWithId[]>([]);
  const [generalSummary, setGeneralSummary] = useState<GeneralSummary | null>(null);
  const [summaryError, setSummaryError] = useState<null | string>(null);
  const lastFetchKeyRef = useRef<null | string>(null);
  const { getBulkSummary, isPending } = SafeHooks.useSafeConversionSummaryBulk();

  useEffect(() => {
    const ids = allSafeIds ?? [];
    if (!companyId) return;
    if (!ids.length) {
      lastFetchKeyRef.current = null;
      return;
    }
    const key = selectionKey(
      companyId,
      valuationId === 'no-valuation' ? undefined : valuationId,
      ids,
      roundingSetting,
    );
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;
    setSummaryError(null);
    getBulkSummary(
      {
        companyId,
        data: {
          safeIds: ids,
          valuationId: valuationId === 'no-valuation' ? undefined : valuationId,
          roundingSetting,
        },
      },
      {
        onSuccess: (items) => {
          const byId = items.map(({ safeId, summary }) => ({ id: safeId, summary }));
          setConversionSummaries(byId);
          setValue('stepOne.conversionDetails', byId);
        },
        // A refused selection (e.g. one that would own 100 % or more of the company) must not
        // leave the previous selection's summary behind: that summary let the user go on and
        // submit a conversion the backend then dropped.
        onError: (error) => {
          setConversionSummaries([]);
          setGeneralSummary(null);
          setValue('stepOne.conversionDetails', undefined);
          setValue('stepOne.conversionSummary', undefined);
          setSummaryError(error.message);
        },
      },
    );
  }, [allSafeIds, companyId, getBulkSummary, roundingSetting, setValue, valuationId]);

  useEffect(() => {
    if (!conversionSummaries.length) return;
    const newPreMoneyValuation = preMoneyValuation || 0;
    const { investment, totalOwned, convertedShares } = conversionSummaries.reduce(
      (acc, item) => {
        acc.investment += item.summary.investment;
        acc.totalOwned += item.summary.capitalOwned;
        acc.convertedShares += item.summary.sharesConverted;
        return acc;
      },
      { investment: 0, totalOwned: 0, convertedShares: 0 },
    );
    const sharePrice = parseFloat(
      (
        (postMoneyValuation || 0) / (toNumber(valuationIssuedShares) + convertedShares || 1)
      ).toFixed(2),
    );

    const newSummary = {
      sharePrice,
      preMoneyValuation: newPreMoneyValuation,
      convertedShares,
      investment,
      totalOwned: parseFloat((totalOwned * 100).toFixed(2)),
    };

    setGeneralSummary(newSummary);

    if (JSON.stringify(conversionSummary) !== JSON.stringify(newSummary)) {
      setValue('stepOne.conversionSummary', newSummary);
    }
  }, [
    conversionSummaries,
    conversionSummary,
    postMoneyValuation,
    preMoneyValuation,
    setValue,
    valuationIssuedShares,
  ]);

  const isLoading =
    isPending || (!!allSafeIds?.length && !conversionSummaries.length && !summaryError);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  useEffect(() => {
    onErrorChange?.(!!summaryError);
  }, [summaryError, onErrorChange]);

  return (
    <>
      <div className="mt-2 flex flex-col gap-3 rounded-md bg-brand-25 p-1 pt-3">
        <span className="text-sm font-[550] text-brand-700">
          <AppFormattedMessage id="GENERAL_CONVERSION_SUMMARY" />
        </span>
        {summaryError ? (
          <div
            className="rounded-md border border-fireside-200 bg-fireside-25 px-4 py-3 text-sm font-[450] text-fireside-700"
            role="alert"
          >
            {summaryError}
          </div>
        ) : isLoading ? (
          <div className="flex items-center gap-2 rounded-md bg-white p-4 text-sm text-gray-500">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-brand-600" />
            <AppFormattedMessage id="LOADING" />
          </div>
        ) : (
          <div className="divide-y-[1px] divide-gray-100 rounded-md bg-white p-3 shadow-sm">
            <div className="flex w-full items-center justify-between py-3">
              <span className="text-xs font-[450] text-gray-700">
                <AppFormattedMessage id="EVENT_SHARE_PRICE_POST_CONVERSION" />
              </span>
              <span className="text-xs font-[450] text-gray-700">
                {currencySign}
                {toMathRound(generalSummary?.sharePrice)}
              </span>
            </div>
            <div className="flex w-full items-center justify-between py-3">
              <span className="text-xs font-[450] text-gray-700">
                <AppFormattedMessage id="SAFE_INVESTMENT_CONVERSION_SUM" />
              </span>
              <span className="text-xs font-[450] text-gray-700">
                {currencySign}
                {generalSummary?.investment?.toLocaleString('en-US')}
              </span>
            </div>
            <div className="flex w-full items-center justify-between py-3">
              <span className="text-xs font-[450] text-gray-700">
                <AppFormattedMessage id="PRE_MONEY_VALUATION" />
              </span>
              <span className="text-xs font-[450] text-gray-700">
                {currencySign}
                {generalSummary?.preMoneyValuation?.toLocaleString('en-US')}
              </span>
            </div>
            <div className="flex w-full items-center justify-between py-3">
              <span className="text-xs font-[450] text-gray-700">
                <AppFormattedMessage id="TOTAL_PERCENT_OWNED_SAFE" />
              </span>
              <span className="text-xs font-[450] text-gray-700">
                {generalSummary?.totalOwned} %
              </span>
            </div>
            <div className="flex w-full items-center justify-between py-3">
              <span className="text-xs font-[450] text-gray-700">
                <AppFormattedMessage id="CONVERTED_SHARES" />
              </span>
              <span className="text-xs font-[450] text-gray-700">
                {toRound(generalSummary?.convertedShares, 0).toLocaleString('en-US')}
              </span>
            </div>
          </div>
        )}
      </div>

      {!summaryError && (
        <div className="mt-2 flex w-full flex-col gap-4 rounded-lg p-4 shadow-sm">
          <span className="text-label-md font-[500] text-gray-500">
            <AppFormattedMessage id="CONVERSION_DETAILS" />
          </span>
          {isLoading ? (
            <ConversionDetailsLoading />
          ) : (
            <div className="flex w-full flex-col gap-2">
              {conversionSummaries.map(({ id: safeId, summary }) => (
                <ConvertSafeItem
                  currencySign={currencySign}
                  key={safeId}
                  safeId={safeId}
                  summary={summary}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};
